import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Send as SendIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    MyLocation as MyLocationIcon,
    PhotoCamera as PhotoCameraIcon,
    Flag as FlagIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, ALT_JOB_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import jobSiteSafetyInspectionService from '../../../services/jobSiteSafetyInspectionService';
import safetyScheduleService from '../../../services/safetyScheduleService';
import { todayKey, weekEndingOf } from '../utils/scheduleDates';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import CameraCapture from '../../shared/CameraCapture';
import useDictation from '../../../hooks/useDictation';
import { buildLocationOptions, findLocationOption } from '../utils/locationUtils';
import { getCurrentLocation, reverseGeocode } from '../utils/geolocation';
import {
    DIVISION_OPTIONS,
    SEVERITY,
    makeSchema,
    headerDomId,
    questionDomId,
    photoLimit,
    photosKey,
    photosOf,
    PHOTO_CAPTION_MAX,
    SIGNATURE_DOM_ID,
    SUPERVISOR_SIGNATURE_DOM_ID,
} from './jobSiteSafetyInspectionSchema';
import jobSiteSafetyInspectionSchemaService from '../../../services/jobSiteSafetyInspectionSchemaService';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

// Answer buttons are coloured by the severity the schema assigns to the option, so a rating an admin
// invents ("Compliant / Minor / Major") reads correctly without anyone touching this file.
const SEVERITY_COLORS = {
    [SEVERITY.GOOD]: 'success',
    [SEVERITY.WARN]: 'warning',
    [SEVERITY.BAD]: 'error',
    [SEVERITY.NEUTRAL]: 'info',
};

// Every photo is shrunk before it touches the draft. A question can now take up to twelve, and a
// straight-from-camera iPad shot is several megabytes of base64 — in IndexedDB, in the submit
// payload and in the PDF. 1200 px on the long side at 0.6 is what the Lift Evaluation form settled
// on for the same reason: plenty to read a hazard on a job site.
const PHOTO_MAX_DIM = 1200;
const PHOTO_QUALITY = 0.6;

function downscaleDataUrl(dataUrl, maxDim = PHOTO_MAX_DIM, quality = PHOTO_QUALITY) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => resolve(dataUrl); // fall back to raw data-url
        img.src = dataUrl;
    });
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => downscaleDataUrl(e.target.result).then(resolve);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const JobSiteSafetyInspectionForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    // The published question set this inspection is being filled out against. Null until it is read
    // from IndexedDB; schemaError explains why when it stays null.
    const [publishedSchema, setPublishedSchema] = useState(null);
    const [schemaError, setSchemaError] = useState(null);

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        status: STATUS_DRAFT,

        // Header / Title Page
        conductedOn: dayjs().format('YYYY-MM-DDTHH:mm'),
        divisions: [],
        // Прораба не подставляем: инспектор и прораб участка — обычно разные люди,
        // а автоподстановка приводила к тому, что в отчёте стоял тот, кто его писал.
        siteSupervisor: '',
        siteSupervisorId: '',
        jobSite: '',
        jobCodes: [],
        exactLocation: '',
        personnelCount: '',
        workDescription: '',

        // Prepared by = current user (autofill, replaces removed manual field)
        preparedById: userId,
        preparedByName: userFullName,

        // All checklist answers keyed by question key (+ _comment / _photo suffixes)
        answers: {},

        // Which published question revision this report was filled out against.
        schemaVersion: null,
        schemaHash: null,

        // Signatures. signedAt пишем в UTC (ISO) в момент нажатия Accept — сервер
        // переведёт в нью-йоркское время, в котором проект хранит все datetime.
        signature: '',
        signatureEmployeeId: userId,
        signatureEmployeeName: userFullName,
        signatureSignedAt: null,

        // Вторая подпись — прораб участка из шапки формы.
        supervisorSignature: '',
        supervisorSignatureSignedAt: null,

        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    });

    const [missingIds, setMissingIds] = useState(() => new Set());
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [supervisorSignatureModalOpen, setSupervisorSignatureModalOpen] = useState(false);
    const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
    const [preparedByModalOpen, setPreparedByModalOpen] = useState(false);
    const [geoBusy, setGeoBusy] = useState(false);
    // Key of the question the camera is adding a photo to.
    const [cameraTarget, setCameraTarget] = useState(null);
    // Caption being written for one photo: { key, index, text }. A dialog rather than a field under
    // every thumbnail — twelve caption fields would stretch each question to twice its height.
    const [captionFor, setCaptionFor] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);
    const supervisorSigPadRef = useRef(null);
    const latestFormRef = useRef(null);
    const hasEditedRef = useRef(false);

    // Dictation - single hook reused for all text fields/comments
    const currentFieldRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000, prime: true,
    });

    // ---- Load -------------------------------------------------------------
    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            let jobsData = [];
            if (db.objectStoreNames.contains(JOB_STORE)) {
                const tx = db.transaction(JOB_STORE, 'readonly');
                jobsData = (await tx.objectStore(JOB_STORE).getAll()) || [];
                setJobs(jobsData);

                let altJobsData = [];
                if (db.objectStoreNames.contains(ALT_JOB_STORE)) {
                    const altTx = db.transaction(ALT_JOB_STORE, 'readonly');
                    altJobsData = (await altTx.objectStore(ALT_JOB_STORE).getAll()) || [];
                }
                setLocationOptions(buildLocationOptions(jobsData, altJobsData));
            }

            let existing = null;
            if (uuid) {
                existing = await jobSiteSafetyInspectionService.getFromIndexedDB(uuid);
                if (existing) {
                    setForm((prev) => ({ ...prev, ...existing, answers: existing.answers || {} }));
                    setSubmitted(existing.status === STATUS_SUBMITTED);
                }
            }

            // A draft keeps the revision it was started on: the questions an inspector already
            // answered must not change wording, scoring or visibility half-way through. Only a brand
            // new inspection picks up the currently published revision.
            try {
                const draftVersion = typeof existing?.schemaVersion === 'number' ? existing.schemaVersion : null;
                const { schema, exact } = draftVersion !== null
                    ? await jobSiteSafetyInspectionSchemaService.getSchemaForDraft(draftVersion)
                    : { schema: await jobSiteSafetyInspectionSchemaService.getActiveSchema(), exact: true };

                if (schema) {
                    setPublishedSchema(schema);

                    // Pin a new inspection to the revision it is starting on. Done with setForm rather
                    // than update() so it does not count as a user edit and conjure an empty draft.
                    // A draft that already carries a revision keeps it, even when we had to fall back
                    // to another one to render right now — losing the original pointer would be worse.
                    if (draftVersion === null) {
                        setForm((prev) => ({ ...prev, schemaVersion: schema.version, schemaHash: schema.hash }));
                    }

                    if (!exact) {
                        setSchemaError({
                            severity: 'warning',
                            message: `This draft was started on question set v${draftVersion}, which is no longer available on this device. `
                                + `It is being shown with v${schema.version} instead, so some questions may differ.`,
                        });
                    }
                } else {
                    setSchemaError({
                        severity: 'error',
                        message: 'The inspection questions have not been synced to this device yet. '
                            + 'Run Sync while online, then open this form again.',
                    });
                }
            } catch (e) {
                console.error('Error loading inspection schema:', e);
                setSchemaError({
                    severity: 'error',
                    message: 'Could not load the inspection questions on this device. Run Sync while online and try again.',
                });
            }

            setLoading(false);
        };
        loadData();
    }, [uuid]);

    // ---- Persistence ------------------------------------------------------
    const saveDraft = async (formData) => {
        try {
            await jobSiteSafetyInspectionService.saveToIndexedDB({ ...formData, updatedAt: new Date().toISOString() });
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    // Keep the latest form available to the unmount-flush without re-subscribing.
    useEffect(() => { latestFormRef.current = form; }, [form]);

    // Debounced autosave on user edits only (never auto-creates an empty draft).
    useEffect(() => {
        if (loading || !hasEditedRef.current) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDraft(latestFormRef.current), 1000);
    }, [form, loading]);

    // Flush any pending save on unmount so the last edit is never lost.
    useEffect(() => () => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        const f = latestFormRef.current;
        if (hasEditedRef.current && f && f.status !== STATUS_SUBMITTED) saveDraft(f);
    }, []);

    const update = (patch) => {
        hasEditedRef.current = true;
        setForm((prev) => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
    };

    // Remove a highlight once the user fills the field/question.
    const clearMissing = (id) => setMissingIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
    });

    const setHeader = (field, value) => { update({ [field]: value }); clearMissing(headerDomId(field)); };

    const setAnswer = (key, value) => {
        update((prev) => ({ ...prev, answers: { ...prev.answers, [key]: value } }));
        // Clear the owning question's highlight when its value OR its comment is filled.
        clearMissing(questionDomId(key.replace(/_(comment|photo|photos)$/, '')));
    };

    // Куда возвращаться. SSI, открытый с карточки My Schedule, возвращает в
    // расписание на ту же неделю — там рэп и видит, что кнопка позеленела.
    const scheduleWorkDate = form.safetyVisit?.workDate;
    const backPath = scheduleWorkDate
        ? (weekEndingOf(scheduleWorkDate) === weekEndingOf(todayKey())
            ? '/safety/schedule'
            : `/safety/schedule?week=${weekEndingOf(scheduleWorkDate)}`)
        : '/safety/forms/job-site-safety-inspection/list';

    // ---- Location / division / GPS ---------------------------------------

    // Локация из списка джобов — «дефолтная»: её адрес есть в справочнике. Вписанная
    // руками (в расписании, через Add location или прямо здесь) — нет, и только для
    // неё форма спрашивает Exact Location, причём обязательно.
    const locationOption = useMemo(
        () => findLocationOption(form.jobSite, locationOptions),
        [form.jobSite, locationOptions]
    );
    const manualLocation = Boolean((form.jobSite || '').trim()) && !locationOption;

    /** Адрес джоба из справочника: сначала по коду работы (надёжно), потом по названию. */
    const jobAddressFor = (option) => {
        if (!option) return '';
        const jobCodes = option.jobCodes || [];
        const label = (option.label || '').trim().toLowerCase();
        const matchedJob =
            jobs.find((j) => jobCodes.includes(j.Job)) ||
            jobs.find((j) => (j.location || j.Location || '').trim().toLowerCase() === label);
        return matchedJob?.locationAddress || matchedJob?.siteAddress || option.siteAddress || '';
    };

    // Выбрали джоб из списка. Division и Work Description остаются ручными.
    const handleLocationChange = (newValue) => {
        if (!newValue) {
            update({ jobSite: '', jobCodes: [] });
            return;
        }
        update((prev) => ({
            ...prev,
            jobSite: newValue.label || '',
            jobCodes: newValue.jobCodes || [],
            // Адрес — строго этого джоба, без «или что было»: иначе адрес, набранный
            // для ручной площадки, молча уехал бы в отчёт под чужим джобом.
            exactLocation: jobAddressFor(newValue),
        }));
        clearMissing(headerDomId('jobSite'));
        clearMissing(headerDomId('exactLocation'));
    };

    // Локацию набирают руками. Совпала с джобом из списка — это он и есть.
    const handleLocationTyped = (text) => {
        const option = findLocationOption(text, locationOptions);
        if (option) {
            handleLocationChange(option);
            return;
        }
        update((prev) => ({
            ...prev,
            jobSite: text,
            jobCodes: [],
            // Уходим с джоба на свою площадку — адрес джоба к ней не относится.
            // Правка уже ручного названия набранный адрес не трогает.
            exactLocation: findLocationOption(prev.jobSite, locationOptions) ? '' : prev.exactLocation,
        }));
        clearMissing(headerDomId('jobSite'));
    };

    // Capture device GPS and reverse-geocode it into the exact-location address.
    const fillAddressFromGps = async () => {
        setGeoBusy(true);
        const pos = await getCurrentLocation();
        if (!pos.ok) {
            setGeoBusy(false);
            Swal.fire('Location', pos.error || 'Could not get your location.', 'info');
            return;
        }
        const geo = await reverseGeocode(pos.lat, pos.lng);
        setGeoBusy(false);
        if (geo.ok && geo.address) {
            update({ exactLocation: geo.address });
            clearMissing(headerDomId('exactLocation'));
        } else {
            Swal.fire('Location', `${geo.error || 'Could not resolve the address.'} Coordinates: ${pos.lat}, ${pos.lng}. Please enter the address manually.`, 'info');
        }
    };

    // ---- Supervisor / signatures ------------------------------------------

    /** Дата и время подписи — всегда по Нью-Йорку, где бы ни стоял планшет. */
    const formatSignedAt = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            month: '2-digit', day: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
        });
    };

    const handleSelectSupervisor = (selectedArray) => {
        setSupervisorModalOpen(false);
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const name = person.FullName || person.name || '';
        const id = person.id || person.ID || '';

        // Сменили прораба — его подпись больше не его. Иначе под новой фамилией
        // остался бы росчерк предыдущего человека.
        const supervisorChanged = name !== form.siteSupervisor || id !== form.siteSupervisorId;

        update({
            siteSupervisor: name,
            siteSupervisorId: id,
            ...(supervisorChanged ? { supervisorSignature: '', supervisorSignatureSignedAt: null } : {}),
        });
    };

    const handleSelectPreparedBy = (selectedArray) => {
        setPreparedByModalOpen(false);
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        update({
            preparedByName: person.FullName || person.name || '',
            preparedById: person.id || person.ID || '',
        });
    };

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;
        update({ signature: sigPadRef.current.toDataURL(), signatureSignedAt: new Date().toISOString() });
        clearMissing(SIGNATURE_DOM_ID);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => update({ signature: '', signatureSignedAt: null });

    const handleSupervisorSignatureAccept = () => {
        if (!supervisorSigPadRef.current) return;
        update({
            supervisorSignature: supervisorSigPadRef.current.toDataURL(),
            supervisorSignatureSignedAt: new Date().toISOString(),
        });
        clearMissing(SUPERVISOR_SIGNATURE_DOM_ID);
        setSupervisorSignatureModalOpen(false);
    };

    const handleClearSupervisorSignature = () =>
        update({ supervisorSignature: '', supervisorSignatureSignedAt: null });

    // ---- Photos (shared CameraCapture: live camera + flip + upload, same as JHA) ----
    const handlePhotoCapture = async (payload) => {
        const key = cameraTarget;
        setCameraTarget(null);
        if (!payload || !key) return;
        try {
            // Downscale before storing (payload.file works for both camera shots and uploads).
            const dataUrl = payload.file ? await fileToDataUrl(payload.file) : await downscaleDataUrl(payload.dataUrl);

            // The limit is re-checked against the latest state, not the render the camera was opened
            // from: two quick shots must not squeeze a thirteenth photo past a limit of twelve.
            const limit = photoLimit(schema.questionByKey[key]);
            update((prev) => {
                const current = photosOf(prev.answers, key);
                if (current.length >= limit) return prev;
                return { ...prev, answers: { ...prev.answers, [photosKey(key)]: [...current, { path: dataUrl, caption: '' }] } };
            });
        } catch (err) {
            console.error('Photo error:', err);
            Swal.fire('Error', 'Could not attach photo', 'error');
        }
    };

    const removeListPhoto = (key, index) => {
        update((prev) => ({
            ...prev,
            answers: { ...prev.answers, [photosKey(key)]: photosOf(prev.answers, key).filter((_, i) => i !== index) },
        }));
    };

    // ---- Photo captions: tap a thumbnail to write or change its caption --------
    const openCaption = (key, index) => {
        const photo = photosOf(form.answers, key)[index];
        if (photo) setCaptionFor({ key, index, text: photo.caption });
    };

    const closeCaption = () => {
        if (isListening && activeField === 'caption') {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
        }
        setCaptionFor(null);
    };

    const saveCaption = () => {
        const { key, index, text } = captionFor;
        const caption = text.trim().slice(0, PHOTO_CAPTION_MAX);
        update((prev) => ({
            ...prev,
            answers: {
                ...prev.answers,
                [photosKey(key)]: photosOf(prev.answers, key).map((photo, i) => (i === index ? { ...photo, caption } : photo)),
            },
        }));
        closeCaption();
    };

    const appendCaption = (transcript) => {
        setCaptionFor((prev) => prev && { ...prev, text: (prev.text ? prev.text.replace(/\s+$/, '') + ' ' : '') + transcript });
    };

    // ---- Dictation --------------------------------------------------------
    const toggleDictation = async (fieldId, apply) => {
        if (!dictationSupported) return;
        if (isListening && activeField === fieldId) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }
        if (isListening) stopDictation();
        currentFieldRef.current = fieldId;
        setActiveField(fieldId);
        await startDictation({
            onResult: (transcript) => { if (transcript && currentFieldRef.current === fieldId) apply(transcript); },
            onEnd: () => { setActiveField(null); currentFieldRef.current = null; },
            onError: () => { setActiveField(null); currentFieldRef.current = null; },
        });
    };

    const appendComment = (key, transcript) => {
        const cur = form.answers[`${key}_comment`] || '';
        setAnswer(`${key}_comment`, cur + (cur ? ' ' : '') + transcript);
    };
    const appendText = (key, transcript) => {
        const cur = form.answers[key] || '';
        setAnswer(key, cur + (cur ? ' ' : '') + transcript);
    };
    const appendHeader = (field, transcript) => {
        update((prev) => ({ ...prev, [field]: (prev[field] || '') + (prev[field] ? ' ' : '') + transcript }));
        clearMissing(headerDomId(field));
    };

    // ---- Derived: score / flags / visibility -----------------------------
    // makeSchema tolerates a null revision (it yields an empty question set), so the header and the
    // handlers below never have to null-check while the schema is still loading.
    const schema = useMemo(() => makeSchema(publishedSchema), [publishedSchema]);
    const score = useMemo(() => schema.computeScore(form.answers), [schema, form.answers]);
    const flags = useMemo(() => schema.computeFlags(form.answers), [schema, form.answers]);

    // ---- Submit -----------------------------------------------------------
    const handleSubmit = async () => {
        const missing = schema.getMissingRequired(form, { manualLocation });
        if (missing.length > 0) {
            setMissingIds(new Set(missing.map((m) => m.id)));
            const list = missing.slice(0, 25).map((m) => `• ${m.label}`).join('<br>');
            const more = missing.length > 25 ? `<br>…and ${missing.length - 25} more` : '';
            await Swal.fire({
                title: `Please complete ${missing.length} required item${missing.length > 1 ? 's' : ''}`,
                html: `<div style="text-align:left;max-height:50vh;overflow:auto;font-size:0.85rem">${list}${more}</div>`,
                icon: 'warning',
            });
            // Scroll to the first unanswered item once the popup is dismissed.
            const el = document.getElementById(missing[0].id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setMissingIds(new Set());

        const result = await Swal.fire({
            title: 'Submit Inspection?',
            text: 'Once submitted, you cannot edit this inspection.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it!',
        });
        if (!result.isConfirmed) return;

        // Отложенное автосохранение не должно сработать посреди сдачи и вернуть
        // черновик, который через секунду будет удалён.
        if (saveTimer.current) clearTimeout(saveTimer.current);

        try {
            // SSI открыт с карточки My Schedule: визит должен дойти до сервера раньше
            // инспекции, чтобы она встала в его строку журнала. Не дошёл (нет сети) —
            // сдача всё равно пройдёт: сервер заведёт визит по той же связи.
            if (form.safetyVisit?.uuid) {
                await safetyScheduleService.processOutbox().catch(() => {});
            }

            const reportData = schema.buildReportData(form);
            const payload = {
                ...form,
                // Джоб из списка: адрес в отчёт — из справочника, спрашивать его в форме
                // не стали. Черновик из My Schedule открывается уже с локацией, мимо
                // выбора в поле, поэтому адрес подставляем здесь, а не только при выборе.
                ...(locationOption ? {
                    jobCodes: form.jobCodes?.length ? form.jobCodes : (locationOption.jobCodes || []),
                    exactLocation: jobAddressFor(locationOption) || form.exactLocation || '',
                } : {
                    exactLocation: (form.exactLocation || '').trim(),
                }),
                // Provenance: which question revision produced this report. The server records it and
                // never rejects on it — a report filled out offline is not something to lose over a
                // version number.
                schemaVersion: schema.version,
                schemaHash: schema.hash,
                status: STATUS_SUBMITTED,
                submittedAt: new Date().toISOString(),
                submittedBy: userId,
                submittedByName: userFullName,
                preparedById: form.preparedById || userId,
                preparedByName: form.preparedByName || userFullName,
                signatureEmployeeId: form.signatureEmployeeId || userId,
                signatureEmployeeName: form.signatureEmployeeName || userFullName,
                reportData,
                score: reportData.score,
                flagged: reportData.flagged,
            };
            const response = await jobSiteSafetyInspectionService.submit(payload);
            if (response.success) {
                // Сданную форму больше не сохраняем как черновик: сохранение при
                // уходе со страницы возвращало её в список черновиков, откуда её
                // можно было сдать второй раз поверх первой.
                hasEditedRef.current = false;
                await jobSiteSafetyInspectionService.deleteFromIndexedDB(form.uuid);

                // Карточка My Schedule зеленеет сразу, без ожидания синка. Только если
                // площадка в форме та же, что на карточке: выбрали в форме другую —
                // это инспекция другой площадки, и сервер её к карточке не привяжет.
                const sameSite = (form.jobSite || '').trim().toLowerCase()
                    === (form.safetyVisit?.location || '').trim().toLowerCase();
                if (form.safetyVisit?.uuid && sameSite) {
                    await safetyScheduleService.markInspectionSubmitted(form.safetyVisit.uuid, {
                        inspectionUuid: form.uuid,
                        scorePercent: reportData.score?.percent ?? null,
                    });
                }

                Swal.fire('Success', 'Inspection submitted successfully!', 'success');
                navigate(backPath);
            } else {
                throw new Error(response.message || 'Submission failed');
            }
        } catch (e) {
            console.error('Error submitting:', e);
            Swal.fire('Error', 'Failed to submit inspection: ' + (e.message || 'Unknown error'), 'error');
        }
    };

    // ---- Renderers --------------------------------------------------------

    /**
     * Один блок подписи: кто подписывает, росчерк и когда расписались.
     * Время показываем по Нью-Йорку — планшет может стоять в другой зоне,
     * а в документе должно быть время площадки.
     */
    const renderSignatureBox = ({
        domId, title, signerName, signature, signedAt, onOpen, onClear, blockedReason = null,
    }) => {
        const isMissing = missingIds.has(domId);
        const signedAtLabel = formatSignedAt(signedAt);
        const blocked = !!blockedReason && !signature;

        return (
            <Box
                id={domId}
                sx={{
                    scrollMarginTop: '80px',
                    border: isMissing ? '1px solid #f44336' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1.5,
                    height: '100%',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    {signerName || '—'}
                </Typography>

                {signature ? (
                    <Box position="relative" display="inline-block">
                        <img
                            src={signature} alt={title}
                            style={{ maxWidth: 200, height: 60, border: '1px solid #ddd', borderRadius: 4, cursor: submitted ? 'default' : 'pointer' }}
                            onClick={() => { if (!submitted) onOpen(); }}
                        />
                        {!submitted && (
                            <IconButton size="small" onClick={onClear} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                ) : (
                    <Button
                        variant="outlined" startIcon={<DrawIcon />} size="small"
                        onClick={onOpen} disabled={submitted || blocked}
                    >
                        Add Signature
                    </Button>
                )}

                <Typography
                    variant="caption"
                    color={blocked ? 'error.main' : 'text.secondary'}
                    sx={{ display: 'block', mt: 1 }}
                >
                    {blocked
                        ? blockedReason
                        : (signedAtLabel ? 'Signed: ' + signedAtLabel + ' (ET)' : 'Not signed yet')}
                </Typography>
            </Box>
        );
    };

    /**
     * A question's photos: thumbnails (tap for the caption, × to drop one) and the Add photo button
     * with how many are left. Rendered only for questions the admin allowed photos on — or that still
     * hold some from an earlier revision, so nothing already taken disappears from view.
     */
    const renderPhotoList = (q) => {
        const limit = photoLimit(q);
        const photos = photosOf(form.answers, q.key);
        if (!limit && photos.length === 0) return null;

        const full = photos.length >= limit;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                {photos.map((photo, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 64, alignSelf: 'flex-start' }}>
                        <Box
                            component="img"
                            src={photo.path}
                            alt={`Photo ${index + 1} for ${schema.qLabel(q)}`}
                            title={photo.caption || (submitted ? '' : 'Tap to add a caption')}
                            onClick={() => openCaption(q.key, index)}
                            sx={{ width: 64, height: 64, objectFit: 'cover', display: 'block', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                        />
                        {!submitted && (
                            <IconButton
                                size="small"
                                onClick={() => removeListPhoto(q.key, index)}
                                aria-label={`Remove photo ${index + 1}`}
                                sx={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                            >
                                <ClearIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        )}
                        {(photo.caption || !submitted) && (
                            <Typography
                                noWrap
                                onClick={() => openCaption(q.key, index)}
                                sx={{ fontSize: '0.65rem', lineHeight: 1.4, mt: 0.25, cursor: 'pointer', color: photo.caption ? 'text.secondary' : 'primary.main' }}
                            >
                                {photo.caption || '+ caption'}
                            </Typography>
                        )}
                    </Box>
                ))}
                {!submitted && limit > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        onClick={() => setCameraTarget(q.key)}
                        disabled={full}
                        sx={{ height: 32 }}
                    >
                        {photos.length ? `Add photo · ${photos.length}/${limit}` : 'Add photo'}
                    </Button>
                )}
            </Box>
        );
    };

    const renderChoiceRow = (q) => {
        const value = form.answers[q.key];
        const comment = form.answers[`${q.key}_comment`] || '';
        const fieldId = `cmt:${q.key}`;
        const listening = isListening && activeField === fieldId;
        const settings = schema.qSettings(q);
        const isMissing = missingIds.has(questionDomId(q.key));
        const options = q.options || [];
        // Which answers demand a written explanation is a property of the answer, not of the word
        // "No" — that is what lets an admin add a failing option to a rating and have it behave.
        const selectedOption = schema.optionFor(q, value);
        const commentRequired = settings.hasComment && !!selectedOption && selectedOption.requireComment;
        const commentPlaceholder = commentRequired
            ? `Comment required for "${value}"`
            : 'Comment / correction date';
        return (
            <Box key={q.key} id={questionDomId(q.key)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5, pb: 1.25, borderBottom: '1px solid #eee', borderLeft: isMissing ? '3px solid #f44336' : '3px solid transparent', bgcolor: isMissing ? 'rgba(244,67,54,0.08)' : 'transparent', pl: 1, borderRadius: 1, scrollMarginTop: '80px' }}>
                <Box sx={{ flex: '0 0 38%', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                        {settings.required && !q.dependsOn && <span style={{ color: '#d32f2f' }}>* </span>}
                        {schema.qLabel(q)}
                    </Typography>
                </Box>
                <Box sx={{ flex: '0 0 auto' }}>
                    <Stack direction="row" spacing={0.5}>
                        {options.map((option) => (
                            <Button
                                key={option.value}
                                variant={value === option.value ? 'contained' : 'outlined'}
                                color={SEVERITY_COLORS[option.severity] || 'primary'}
                                size="small"
                                onClick={() => setAnswer(q.key, option.value)}
                                disabled={submitted}
                                sx={{ minWidth: option.value.length > 2 ? 50 : 42, height: 32, fontSize: '0.7rem', p: '4px 8px' }}
                            >
                                {option.value}
                            </Button>
                        ))}
                    </Stack>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                        fullWidth multiline size="small"
                        placeholder={commentPlaceholder}
                        value={comment}
                        onChange={(e) => setAnswer(`${q.key}_comment`, e.target.value)}
                        disabled={submitted}
                        error={commentRequired && !comment}
                        InputProps={{
                            endAdornment: !submitted && dictationSupported && (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                    <IconButton size="small" onClick={() => toggleDictation(fieldId, (t) => appendComment(q.key, t))} color={listening ? 'error' : 'primary'}>
                                        {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '3px 6px' }, '& .MuiInputBase-root': { alignItems: 'flex-start', p: 0 }, '& .MuiOutlinedInput-root': { p: 0 } }}
                    />
                    {/* Under the comment: a photo is evidence for the same finding the comment describes. */}
                    {renderPhotoList(q)}
                </Box>
            </Box>
        );
    };

    const renderTextRow = (q, withPhoto) => {
        const value = form.answers[q.key] || '';
        const photo = form.answers[`${q.key}_photo`] || '';
        const fieldId = `txt:${q.key}`;
        const listening = isListening && activeField === fieldId;
        const settings = schema.qSettings(q);
        const isMissing = missingIds.has(questionDomId(q.key));
        return (
            <Box key={q.key} id={questionDomId(q.key)} sx={{ mb: 1.5, borderLeft: isMissing ? '3px solid #f44336' : '3px solid transparent', bgcolor: isMissing ? 'rgba(244,67,54,0.08)' : 'transparent', pl: 1, borderRadius: 1, scrollMarginTop: '80px' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                    {settings.required && !q.dependsOn && <span style={{ color: '#d32f2f' }}>* </span>}
                    {schema.qLabel(q)}
                </Typography>
                <TextField
                    fullWidth multiline minRows={2} size="small"
                    value={value}
                    onChange={(e) => setAnswer(q.key, e.target.value)}
                    disabled={submitted}
                    InputProps={{
                        endAdornment: !submitted && dictationSupported && (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                <IconButton size="small" onClick={() => toggleDictation(fieldId, (t) => appendText(q.key, t))} color={listening ? 'error' : 'primary'}>
                                    {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {/* "Free text + photo" takes its photos in the list below, like any question. A single
                    photo from a draft started before that is still shown, so nothing taken is lost. */}
                {withPhoto && photo && (
                    <Box sx={{ mt: 0.75 }}>
                        <Box position="relative" display="inline-block">
                            <img src={photo} alt="attachment" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 4, border: '1px solid #ddd' }} />
                            {!submitted && (
                                <IconButton size="small" onClick={() => setAnswer(`${q.key}_photo`, '')} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                )}
                {renderPhotoList(q)}
            </Box>
        );
    };

    const renderQuestion = (q) => {
        if (!schema.isVisible(q, form.answers)) return null;
        switch (q.type) {
            // All three choice types render the same way now: the buttons, their points and their
            // severity all come from the question's own option list.
            case 'ynna':
            case 'gate':
            case 'rating':
                return renderChoiceRow(q);
            case 'text':
                return renderTextRow(q, false);
            case 'textPhoto':
                return renderTextRow(q, true);
            default:
                // An unknown type means this device is running an older build than the schema it was
                // given. Render what we can rather than dropping the question silently.
                return renderTextRow(q, false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ height: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    // No question set on the device means there is nothing to fill in. Say so plainly instead of
    // showing an empty form that would submit an empty report.
    if (!publishedSchema) {
        return (
            <Box sx={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
                <Typography variant="h6" color="error">Inspection questions are not available</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, textAlign: 'center' }}>
                    {schemaError?.message || 'The inspection questions have not been synced to this device yet.'}
                </Typography>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>
                    {scheduleWorkDate ? 'Back to schedule' : 'Back to list'}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(backPath)} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>JOB SITE SAFETY INSPECTION</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {score.percent !== null && (
                            <Chip label={`Score: ${score.percent}% (${score.earned}/${score.possible})`} size="small" color="primary" variant="outlined" />
                        )}
                        {flags.length > 0 && (
                            <Chip icon={<FlagIcon />} label={flags.length} size="small" color="error" />
                        )}
                        {schema.version !== null && (
                            <Tooltip title={`Question set revision ${schema.version}`}>
                                <Chip label={`v${schema.version}`} size="small" variant="outlined" />
                            </Tooltip>
                        )}
                        <Chip label={submitted ? 'Submitted' : 'Draft'} size="small" sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />
                    </Stack>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* The revision on screen is not the one this draft was started on. */}
                    {schemaError && (
                        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
                            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                <Typography variant="caption" color="warning.dark">{schemaError.message}</Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Title Page / Inspection Information */}
                    <Card elevation={1} sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>Inspection Information</Typography>
                            <Grid container rowSpacing={1.5} columnSpacing={1.5}>
                                <Grid item xs={12} sm={6} md={4} id={headerDomId('conductedOn')} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Conducted on *" type="datetime-local"
                                        value={form.conductedOn}
                                        onChange={(e) => setHeader('conductedOn', e.target.value)}
                                        disabled={submitted}
                                        InputLabelProps={{ shrink: true }}
                                        error={missingIds.has(headerDomId('conductedOn'))}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} id={headerDomId('jobSite')} sx={{ scrollMarginTop: '80px' }}>
                                    {/* freeSolo — как Add location в My Schedule: площадки может не
                                        быть в списке джобов. Без него локация, вписанная руками в
                                        расписании, открывалась в форме пустым полем. */}
                                    <Autocomplete
                                        freeSolo
                                        options={locationOptions}
                                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                                        value={locationOption || (form.jobSite ? form.jobSite : null)}
                                        onChange={(e, v) => {
                                            if (v && typeof v === 'object') handleLocationChange(v);
                                            else handleLocationTyped(v || '');
                                        }}
                                        onInputChange={(e, v, reason) => { if (reason === 'input') handleLocationTyped(v); }}
                                        disabled={submitted}
                                        renderInput={(params) => <TextField {...params} size="small" label="Location *" placeholder="Select a job site or type a location" error={missingIds.has(headerDomId('jobSite'))} />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Autocomplete
                                        multiple
                                        options={DIVISION_OPTIONS}
                                        getOptionLabel={(o) => o.label}
                                        value={DIVISION_OPTIONS.filter((o) => (form.divisions || []).includes(o.code))}
                                        onChange={(e, v) => setHeader('divisions', v.map((o) => o.code))}
                                        disabled={submitted}
                                        renderInput={(params) => <TextField {...params} size="small" label="Division (If Known)" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} id={headerDomId('siteSupervisor')} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Site Supervisor *"
                                        value={form.siteSupervisor}
                                        onClick={() => !submitted && setSupervisorModalOpen(true)}
                                        error={missingIds.has(headerDomId('siteSupervisor'))}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSupervisorModalOpen(true)}><PersonSearchIcon fontSize="small" /></IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        disabled={submitted}
                                        placeholder="Select supervisor"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} id={headerDomId('personnelCount')} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Number of Personnel Onsite *" type="number"
                                        value={form.personnelCount}
                                        onChange={(e) => setHeader('personnelCount', e.target.value)}
                                        disabled={submitted}
                                        error={missingIds.has(headerDomId('personnelCount'))}
                                        helperText="Include subcontractors"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth size="small" label="Prepared by"
                                        value={form.preparedByName}
                                        onClick={() => !submitted && setPreparedByModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setPreparedByModalOpen(true)}><PersonSearchIcon fontSize="small" /></IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        disabled={submitted}
                                        placeholder="Select preparer"
                                    />
                                </Grid>
                                <Grid item xs={12} id={headerDomId('workDescription')} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Brief Description of Work being performed *" multiline minRows={2}
                                        value={form.workDescription}
                                        onChange={(e) => setHeader('workDescription', e.target.value)}
                                        disabled={submitted}
                                        error={missingIds.has(headerDomId('workDescription'))}
                                        InputProps={{
                                            endAdornment: !submitted && dictationSupported && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                    <IconButton size="small" onClick={() => toggleDictation('hdr:workDescription', (t) => appendHeader('workDescription', t))} color={isListening && activeField === 'hdr:workDescription' ? 'error' : 'primary'}>
                                                        {isListening && activeField === 'hdr:workDescription' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                {/* Адрес — только для локации, вписанной руками: у джоба из списка
                                    он есть в справочнике и уходит в отчёт сам (см. handleSubmit). */}
                                {manualLocation && (
                                    <Grid item xs={12} id={headerDomId('exactLocation')} sx={{ scrollMarginTop: '80px' }}>
                                        <TextField
                                            fullWidth size="small" label="Exact Location (Address) *" multiline minRows={1}
                                            value={form.exactLocation || ''}
                                            onChange={(e) => setHeader('exactLocation', e.target.value)}
                                            disabled={submitted}
                                            error={missingIds.has(headerDomId('exactLocation'))}
                                            placeholder="Street address of this site — type it or use the location button"
                                            helperText="This location is not on the job list, so its address is required"
                                            InputProps={{
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                        <Tooltip title="Fill address from device location">
                                                            <span>
                                                                <IconButton size="small" onClick={fillAddressFromGps} disabled={geoBusy} color="primary">
                                                                    {geoBusy ? <CircularProgress size={18} /> : <MyLocationIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Sections */}
                    {schema.sections.map((section) => {
                        const visibleQs = section.questions.filter((q) => schema.isVisible(schema.questionByKey[q.key] || q, form.answers));
                        if (visibleQs.length === 0) return null;
                        return (
                            <Box key={section.key} sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.light', pb: 0.5 }}>
                                    {section.title}
                                </Typography>
                                {section.questions.map((q) => renderQuestion(q))}
                            </Box>
                        );
                    })}

                    {/* Flagged preview */}
                    {flags.length > 0 && (
                        <Card elevation={1} sx={{ mb: 2, borderLeft: '4px solid #d32f2f' }}>
                            <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                                    <FlagIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Flagged Items ({flags.length})
                                </Typography>
                                <Stack spacing={0.5}>
                                    {flags.map((f, i) => (
                                        <Typography key={i} variant="caption" color="text.secondary">
                                            <strong>{f.sectionTitle}:</strong> {f.number ? `${f.number}. ` : ''}{f.label} — <span style={{ color: '#d32f2f' }}>{f.value}</span>
                                        </Typography>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Signatures — инспектор и прораб участка, у каждого своя дата/время */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Signatures</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    {renderSignatureBox({
                                        domId: SIGNATURE_DOM_ID,
                                        title: 'Inspector Signature *',
                                        signerName: form.signatureEmployeeName,
                                        signature: form.signature,
                                        signedAt: form.signatureSignedAt,
                                        onOpen: () => setSignatureModalOpen(true),
                                        onClear: handleClearSignature,
                                    })}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    {renderSignatureBox({
                                        domId: SUPERVISOR_SIGNATURE_DOM_ID,
                                        title: 'Site Supervisor Signature *',
                                        signerName: form.siteSupervisor,
                                        signature: form.supervisorSignature,
                                        signedAt: form.supervisorSignatureSignedAt,
                                        onOpen: () => setSupervisorSignatureModalOpen(true),
                                        onClear: handleClearSupervisorSignature,
                                        // Подписывать некому, пока прораб не выбран в шапке.
                                        blockedReason: form.siteSupervisor
                                            ? null
                                            : 'Select the site supervisor in the header first',
                                    })}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate(backPath)}>Cancel</Button>
                    {!submitted && (
                        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>Submit</Button>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <CameraCapture
                open={Boolean(cameraTarget)}
                onClose={() => setCameraTarget(null)}
                onCapture={handlePhotoCapture}
            />

            <Dialog open={Boolean(captionFor)} onClose={closeCaption} fullWidth maxWidth="sm">
                {captionFor && (() => {
                    const photos = photosOf(form.answers, captionFor.key);
                    const photo = photos[captionFor.index];
                    const listening = isListening && activeField === 'caption';
                    return (
                        <>
                            <DialogTitle sx={{ pb: 1 }}>
                                Photo {captionFor.index + 1} of {photos.length}
                            </DialogTitle>
                            <DialogContent>
                                {photo && (
                                    <Box
                                        component="img"
                                        src={photo.path}
                                        alt={`Photo ${captionFor.index + 1}`}
                                        sx={{ display: 'block', boxSizing: 'border-box', maxWidth: '100%', maxHeight: '50vh', mx: 'auto', mb: 2, borderRadius: 1, border: '1px solid #ddd' }}
                                    />
                                )}
                                <TextField
                                    fullWidth multiline minRows={2} size="small"
                                    label="Caption"
                                    autoFocus={!submitted}
                                    value={captionFor.text}
                                    onChange={(e) => setCaptionFor((prev) => prev && { ...prev, text: e.target.value })}
                                    disabled={submitted}
                                    inputProps={{ maxLength: PHOTO_CAPTION_MAX }}
                                    InputProps={{
                                        endAdornment: !submitted && dictationSupported && (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                <IconButton size="small" onClick={() => toggleDictation('caption', appendCaption)} color={listening ? 'error' : 'primary'}>
                                                    {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </DialogContent>
                            <DialogActions>
                                {submitted ? (
                                    <Button onClick={closeCaption}>Close</Button>
                                ) : (
                                    <>
                                        <Button onClick={closeCaption}>Cancel</Button>
                                        <Button
                                            variant="contained"
                                            onClick={saveCaption}
                                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                                        >
                                            Save
                                        </Button>
                                    </>
                                )}
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {supervisorModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={supervisorModalOpen}
                    onClose={() => setSupervisorModalOpen(false)}
                    onSelect={handleSelectSupervisor}
                    title="Select Site Supervisor"
                    singleSelect={true}
                    alreadySelected={form.siteSupervisor ? [{ FullName: form.siteSupervisor }] : []}
                />
            )}

            {preparedByModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={preparedByModalOpen}
                    onClose={() => setPreparedByModalOpen(false)}
                    onSelect={handleSelectPreparedBy}
                    title="Select Prepared By"
                    singleSelect={true}
                    alreadySelected={form.preparedByName ? [{ FullName: form.preparedByName }] : []}
                />
            )}

            {signatureModalOpen && (
                <SignatureModalWithAuto
                    open={signatureModalOpen}
                    onClose={() => setSignatureModalOpen(false)}
                    sigPadRef={sigPadRef}
                    employeeName={form.signatureEmployeeName}
                    onAccept={handleSignatureAccept}
                    existingSignature={form.signature}
                />
            )}

            {supervisorSignatureModalOpen && (
                <SignatureModalWithAuto
                    open={supervisorSignatureModalOpen}
                    onClose={() => setSupervisorSignatureModalOpen(false)}
                    sigPadRef={supervisorSigPadRef}
                    employeeName={form.siteSupervisor}
                    onAccept={handleSupervisorSignatureAccept}
                    existingSignature={form.supervisorSignature}
                />
            )}
        </Box>
    );
};

export default JobSiteSafetyInspectionForm;
